import { useEffect, useState } from "react";
import { Building, Bookmark, X, Calendar, Eye, Info } from "lucide-react";
import { api } from "../lib/api";

/**
 * Engagement panel for the Lead detail page. Answers "what did I share with
 * this lead, what did they do about it, and what patterns should I notice?"
 */
export default function LeadEngagement({ leadId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get(`/leads/${leadId}/shared-properties`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [leadId]);

  if (loading) return null;
  if (!data || (data.collections.length === 0 && data.feedback.length === 0 && data.view_count === 0)) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
            <Building className="h-4 w-4 text-slate-500" />
          </div>
          <div className="min-w-0">
            <div className="font-heading font-semibold text-slate-900">No properties shared yet</div>
            <p className="text-sm text-slate-500 mt-0.5">Share listings and every view, save, or showing-request feeds this lead's score in real time.</p>
          </div>
        </div>
      </div>
    );
  }

  const saves = data.feedback.filter(f => f.reaction === "saved").length;
  const passes = data.feedback.filter(f => f.reaction === "passed").length;
  const showings = data.feedback.filter(f => f.reaction === "showing_requested").length;

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="lead-engagement">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Listings engagement</h2>
          <p className="text-xs text-slate-500 mt-0.5">{data.collections.length} share{data.collections.length === 1 ? "" : "s"} · {data.view_count} view{data.view_count === 1 ? "" : "s"} · {saves} saved · {showings} showing{showings === 1 ? "" : "s"} requested · {passes} passed</p>
        </div>
      </div>

      {data.pattern_hint && (
        <div className="mx-6 mt-4 rounded-md border-l-4 border-amber-400 bg-amber-50 p-3 flex items-start gap-2 text-sm text-amber-900" data-testid="engagement-hint">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <div><strong>Pattern:</strong> {data.pattern_hint}. Consider adjusting your next shares.</div>
        </div>
      )}

      <div className="p-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={Eye}      label="Views"    value={data.view_count} />
        <Stat icon={Bookmark} label="Saves"    value={saves} />
        <Stat icon={Calendar} label="Showings" value={showings} highlight={showings > 0} />
        <Stat icon={X}        label="Passes"   value={passes} />
      </div>

      {data.feedback.length > 0 && (
        <div className="border-t border-slate-100">
          <ul className="divide-y divide-slate-100">
            {data.feedback.slice(0, 5).map(f => (
              <li key={f.id} className="px-6 py-3 flex items-baseline justify-between gap-4 text-sm">
                <div className="min-w-0">
                  <span className={`text-xs font-semibold uppercase tracking-wider mr-2 ${
                    f.reaction === "showing_requested" ? "text-emerald-700" :
                    f.reaction === "saved" ? "text-blue-700" :
                    f.reaction === "passed" ? "text-red-700" : "text-slate-500"
                  }`}>
                    {f.reaction.replace("_", " ")}
                  </span>
                  {f.reason_codes?.length > 0 && (
                    <span className="text-slate-600">— {f.reason_codes.map(r => r.replace("_", " ")).join(", ")}</span>
                  )}
                  {f.comment && <span className="text-slate-500 italic ml-2">"{f.comment}"</span>}
                </div>
                <time className="text-xs text-slate-500 shrink-0">{new Date(f.created_at).toLocaleDateString()}</time>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`rounded-md p-3 ${highlight ? "bg-emerald-50 border border-emerald-200" : "bg-slate-50 border border-slate-100"}`}>
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`mt-1 font-heading text-2xl font-bold tabular-nums ${highlight ? "text-emerald-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
